import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Icon, Popup } from "semantic-ui-react";

import { AppointmentStatusCell, CustomerCard, ReactTable, TableDatePicker, TableDropdownSelector } from "components";
import { usePermanentLocations, usePermanentRealtimeDealers, useSelectedLocation } from "components/DealersAndLocationsDropdown/hooks";
import { useRegisterNavAction } from "hooks";
import { APPOINTMENT_NOTE_TYPES } from "models";
import { useBackordersDashboardFilters } from "modules/BackordersDashboard/components/BackordersDashboardFilters/hooks";
import "modules/BackordersDashboard/components/BackordersDashboardTable/BackordersDashboardTable.scss";
import {
  handleAppointmentDateRangeChange,
  handleBODateRangeChange,
  handlePageSizeChange,
  handleRegVinFilterChange,
  useBackordersDashboardTablePaginationChange
} from "modules/BackordersDashboard/components/BackordersDashboardTable/hooks";
import { useBackordersQuery } from "modules/BackordersDashboard/hooks";
import { getFormattedDate } from "modules/LeadsDashboard/util";
import { handleCopyToClipboard } from "modules/ManufacturerDashboard/utils";
import { REG_VIN_FILTER } from "modules/WarrantyDashboard/hooks";
import { PATHS } from "router/paths";

import type { AppointmentNote } from "models";

// TODO: find a solution to this any
const columnHelper = createColumnHelper<any>();

export const BackordersDashboardTable = () => {
  const { t } = useTranslation();

  const { dealers } = usePermanentRealtimeDealers();
  const { selectedLocation } = useSelectedLocation();
  const { locations } = usePermanentLocations();
  const {
    page: currentPage,
    page_size: pageSize,
    appointment_date_from,
    appointment_date_to,
    backorder_date_from,
    backorder_date_to,
    dealer_ids,
    location_ids,
    reg_vin_filter
  } = useBackordersDashboardFilters();
  const { backordersData, nbItems, nbPages, isFetching, refetch: refetchBackorders } = useBackordersQuery();
  const { handlePaginationChange } = useBackordersDashboardTablePaginationChange();
  const tablePagination = nbPages > 1 ? { pageIndex: currentPage - 1, pageSize } : undefined;

  const regHeaderOptions = [
    { key: REG_VIN_FILTER.Reg, text: t("v8_reg_number", "Reg #"), value: REG_VIN_FILTER.Reg },
    { key: REG_VIN_FILTER.Vin, text: t("v8_vin_number", "Vin #"), value: REG_VIN_FILTER.Vin }
  ];

  useRegisterNavAction(() => (dealer_ids.length || location_ids.length) && refetchBackorders());

  const columns = [
    columnHelper.accessor(row => row, {
      id: "backorders_appointment_date",
      header: () => (
        <div className="BackordersDashboardTable__header-filters">
          <span className="no-wrap-text BackordersDashboardTable__table-header-text">{t("v8_appointment_date", "Appointment Date")}</span>
          <TableDatePicker
            clearable
            dateStart={appointment_date_from}
            dateEnd={appointment_date_to}
            onChange={handleAppointmentDateRangeChange}
            inputClassName="backorders-appointment-date-input"
          />
        </div>
      ),
      size: 20,
      cell: ({
        row: {
          original: { time_car_app }
        }
      }) => <span className="no-wrap-text"> {getFormattedDate(time_car_app)} </span>
    }),

    columnHelper.accessor(row => row, {
      id: "backorders_status",
      header: () => <span className="no-wrap-text BackordersDashboardTable__table-header-text">{t("v8_status", "Status")}</span>,
      size: 30,
      cell: ({
        row: {
          original: { appointment_status_identifier, customer_communication, car_check_paused_at, car_out_of_shop, car_in_shop }
        }
      }) => (
        <AppointmentStatusCell
          shadow={false}
          value={{
            id: appointment_status_identifier,
            customcom_status: customer_communication?.status,
            car_check_paused_at: car_check_paused_at,
            car_out_of_shop: car_out_of_shop,
            car_in_shop: car_in_shop
          }}
          statuses={selectedLocation?.statuses}
        />
      )
    }),

    columnHelper.accessor(row => row, {
      id: "backorders_location",
      header: () => <span className="no-wrap-text BackordersDashboardTable__table-header-text">{t("v8_location", "Location")}</span>,
      size: 50,
      cell: ({
        row: {
          original: { dealer_location_id }
        }
      }) => {
        const location = locations.find(location => location.id === dealer_location_id);
        const dealer = dealers.find(dealer => dealer.id === location?.dealer_id);

        return (
          <span className="no-wrap-text">
            {dealer?.name}, {location?.name}
          </span>
        );
      }
    }),

    columnHelper.accessor(row => row, {
      id: "backorders_bo_date",
      header: () => (
        <div className="BackordersDashboardTable__header-filters">
          <span className="no-wrap-text BackordersDashboardTable__table-header-text">{t("v8_bo_date", "BO Date")}</span>
          <TableDatePicker
            clearable
            dateStart={backorder_date_from}
            dateEnd={backorder_date_to}
            onChange={handleBODateRangeChange}
            inputClassName="backorders-bo-date-input"
          />
        </div>
      ),
      size: 50,
      cell: ({
        row: {
          original: { notes }
        }
      }) => {
        const boNote = notes?.findLast((n: AppointmentNote) => n.appointment_note_type_id === APPOINTMENT_NOTE_TYPES.BackOrder);
        return <span className="no-wrap-text"> {getFormattedDate(boNote?.backorder_date)} </span>;
      }
    }),

    columnHelper.accessor(row => row, {
      id: "backorders_wo",
      header: () => <span className="no-wrap-text BackordersDashboardTable__table-header-text">WO # {backordersData.length}</span>,
      size: 50,
      cell: ({
        row: {
          original: { wo_nr, id }
        }
      }) => (
        <p>
          <Link to={`${PATHS.APPOINTMENTS}/${id}`}>
            <Icon className="arrow up right from square" />
            {wo_nr}
          </Link>
        </p>
      )
    }),

    columnHelper.accessor(row => row, {
      id: "backorders_reg_number",
      header: () => (
        <div className="BackordersDashboardTable__header-filters">
          <TableDropdownSelector
            trigger={
              <span className="no-wrap-text BackordersDashboardTable__table-header-text">
                {reg_vin_filter === REG_VIN_FILTER.Reg ? `${t("v8_reg_number", "Reg #")}` : `${t("v8_vin_number", "Vin #")} `}
              </span>
            }
            options={regHeaderOptions}
            selected={[String(reg_vin_filter)]}
            handleOnSelect={(selectedItem: string[]) => handleRegVinFilterChange(Number(selectedItem.at(0)))}
          />
        </div>
      ),
      size: 50,
      cell: ({
        row: {
          original: { car_id, is_lease_company, reg_number, vin_nr }
        }
      }) => {
        const isVinFilterActive = reg_vin_filter === REG_VIN_FILTER.Vin;
        const displayedField = isVinFilterActive ? vin_nr : reg_number;

        const renderContent = () => {
          if (isVinFilterActive) {
            return (
              <div
                className="BackordersDashboardTable__vin-container"
                onClick={() => handleCopyToClipboard(displayedField.toString(), t("v8_vin_nr_copied", "Vin # copied to clipboard."))}
              >
                {vin_nr && <Icon className="copy outline" />}
                <p>{displayedField}</p>
              </div>
            );
          }

          return (
            <Link to={`${PATHS.CAR_DETAIL}/${car_id}`}>
              <Icon className="arrow up right from square" />
              {reg_number}
              {is_lease_company && <Icon name="building" />}
            </Link>
          );
        };

        return <p className="BackordersDashboardTable__content">{renderContent()}</p>;
      }
    }),

    columnHelper.accessor(row => row, {
      id: "backorders_bo_note",
      header: () => <span className="no-wrap-text BackordersDashboardTable__table-header-text">{t("v8_bo_note", "BO Note")}</span>,
      maxSize: 120,
      cell: ({
        row: {
          original: { notes }
        }
      }) => {
        const boNote = notes?.findLast((n: AppointmentNote) => n.appointment_note_type_id === APPOINTMENT_NOTE_TYPES.BackOrder);
        if (boNote?.note === "") return "";

        return (
          <Popup
            hoverable
            content={boNote?.note}
            trigger={
              <div className="BackordersDashboardTable__bo-note-container">
                <p className="BackordersDashboardTable__table-ellipsis">{boNote?.note}</p>
              </div>
            }
          />
        );
      }
    }),

    columnHelper.accessor(row => row, {
      id: "backorders_customer",
      header: () => <div />,
      size: 20,
      cell: ({
        row: {
          original: {
            customer_contract,
            customer_driver,
            customer_owner,
            driver_opted_out_from_importing,
            owner_opted_out_from_importing,
            contractor_opted_out_from_importing
          }
        }
      }) => (
        <div className="BackordersDashboardTable__table-action-buttons-container">
          <CustomerCard
            contractor={customer_contract}
            driver={customer_driver}
            owner={customer_owner}
            driverOptedOutFromImporting={driver_opted_out_from_importing}
            ownerOptedOutFromImporting={owner_opted_out_from_importing}
            contractorOptedOutFromImporting={contractor_opted_out_from_importing}
          />
        </div>
      )
    })
  ];

  return (
    <div className="BackordersDashboardTable">
      <ReactTable
        isFetching={isFetching}
        columns={columns}
        data={backordersData}
        pageCount={nbPages}
        pageSize={pageSize}
        totalItems={nbItems}
        pagination={tablePagination}
        onPaginationChange={handlePaginationChange}
        onChangePageSize={handlePageSizeChange}
      />
    </div>
  );
};
